import React, {useRef} from 'react';
import {SafeAreaView} from 'react-native-safe-area-context';
import {RFValue} from 'react-native-responsive-fontsize';
import {useIsFocused, useRoute} from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {Header} from '../../../../components/Interface/Headers/Header';
import {Button} from '../../../../components/Interface/Forms/Buttons/Button';
import {useGlobal} from '../../../../context/global';
import {Maintenance} from '../../../../components/Interface/Maintenance';
import {PPConsultaDTO, PPDebitoDTO} from '../../../../dtos/ProntoPagueiDTO';

import {
  Container,
  Content,
  Footer,
  FooterTotal,
  FooterTotalLabel,
  FooterTotalValue,
  Section,
  SectionScroll,
  SelectAllBar,
  MasterCheckBox,
  SelectAllText,
  SelectAllCount,
  SelectAllDivider,
  SectionTitle,
  TitleRow,
  TitleText,
  SenatranBadge,
  SenatranSelo,
  SenatranTextWrapper,
  SenatranLabel,
  SenatranTitle,
} from './styles';

import {formatCurrency} from './utils';
import {useDebitSelection} from './hooks/useDebitSelection';
import {useVerParcelas} from './hooks/useVerParcelas';
import {DebitItem} from './components/DebitItem';

const seloImage = require('../../../../assets/images/selo.webp');
import {rastrear} from '../../../../services/rastrear';

//import {BannerAlert} from '../../../../components/Interface/Alerts/BannerAlert';
interface Params {
  consulta: PPConsultaDTO;
  placa: string;
  uf: string;
}

export function PP_Debits() {
  const routes = useRoute();
  const {fetchPixDebitoVeicular, pixDebitoVeicular, disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);
  const isFocused = useIsFocused();

  const {consulta, placa} = routes.params as Params;

  const {resultado} = consulta;
  const consultaId = consulta.consulta_id.$oid;
  const {uuid} = resultado;

  const ipva = resultado.debitos.ipva;
  const lice = resultado.debitos.licenciamento;
  const infr = resultado.debitos.multas;
  const outr = resultado.debitos.outros;
  const dpvat = resultado.debitos.dpvat;

  const todosDebitos: PPDebitoDTO[] = [
    ...ipva,
    ...lice,
    ...infr,
    ...outr,
    ...dpvat,
  ];

  const {
    selectedDebits,
    selectedSet,
    totalSelectedValue,
    masterState,
    handleToggleAll,
    handleSelectDebit,
  } = useDebitSelection(todosDebitos);

  const {handleVerParcelas} = useVerParcelas({
    selectedDebits,
    totalSelectedValue,
    consultaId,
    uuid,
    placa,
    pixDebitoVeicular,
  });

  const fetchedRef = useRef(false);

React.useEffect(() => {
  if (!fetchedRef.current) {
    fetchPixDebitoVeicular();
    fetchedRef.current = true;
    rastrear({status_tela: 'detalhesMultas'});
  }
}, [isFocused]);

  if (isDisabled('multas')) {
    return <Maintenance />;
  }

  return (
    <Container>
      <Header icon="chevron-left" />
      <Content>
        <SectionScroll showsVerticalScrollIndicator={false}>
          <TitleRow>
            <TitleText numberOfLines={1} adjustsFontSizeToFit>
              O que deseja pagar?
            </TitleText>
            <SenatranBadge>
              <SenatranSelo source={seloImage} resizeMode="contain" />
              <SenatranTextWrapper>
                <SenatranLabel>Credenciados pelo</SenatranLabel>
                <SenatranTitle>SENATRAN</SenatranTitle>
              </SenatranTextWrapper>
            </SenatranBadge>
          </TitleRow>

          <SelectAllBar activeOpacity={0.7} onPress={handleToggleAll}>
            <MasterCheckBox state={masterState}>
              {masterState === 'all' && (
                <MaterialCommunityIcons
                  name="check"
                  size={RFValue(14)}
                  color="#fff"
                />
              )}
              {masterState === 'some' && (
                <MaterialCommunityIcons
                  name="minus"
                  size={RFValue(14)}
                  color="#fff"
                />
              )}
            </MasterCheckBox>
            <SelectAllText>Selecionar todos</SelectAllText>
            <SelectAllCount>
              {selectedDebits.length} de {todosDebitos.length}
            </SelectAllCount>
          </SelectAllBar>

          <SelectAllDivider />

          {lice.length > 0 && (
            <Section>
              <SectionTitle>Licenciamento</SectionTitle>
              {lice.map(item => (
                <DebitItem
                  key={item.uid}
                  item={item}
                  isSelected={selectedSet.has(item)}
                  onPress={handleSelectDebit}
                />
              ))}
            </Section>
          )}

          {ipva.length > 0 && (
            <Section>
              <SectionTitle>IPVA {ipva[0]?.ano}</SectionTitle>
              {ipva.map(item => (
                <DebitItem
                  key={item.uid}
                  item={item}
                  isSelected={selectedSet.has(item)}
                  onPress={handleSelectDebit}
                />
              ))}
            </Section>
          )}

          {infr.length > 0 && (
            <Section>
              <SectionTitle>Multas</SectionTitle>
              {infr.map(item => (
                <DebitItem
                  key={item.uid}
                  item={item}
                  isSelected={selectedSet.has(item)}
                  onPress={handleSelectDebit}
                />
              ))}
            </Section>
          )}

          {dpvat.length > 0 && (
            <Section>
              <SectionTitle>DPVAT</SectionTitle>
              {dpvat.map(item => (
                <DebitItem
                  key={item.uid}
                  item={item}
                  isSelected={selectedSet.has(item)}
                  onPress={handleSelectDebit}
                />
              ))}
            </Section>
          )}

          {outr.length > 0 && (
            <Section>
              <SectionTitle>Outros débitos</SectionTitle>
              {outr.map(item => (
                <DebitItem
                  key={item.uid}
                  item={item}
                  isSelected={selectedSet.has(item)}
                  onPress={handleSelectDebit}
                />
              ))}
            </Section>
          )}
        </SectionScroll>
      </Content>

      <Footer>
        <SafeAreaView>
          <FooterTotal>
            <FooterTotalLabel>Total selecionado</FooterTotalLabel>
            <FooterTotalValue>
              {formatCurrency(totalSelectedValue)}
            </FooterTotalValue>
          </FooterTotal>
          <Button
            title="Continuar"
            onPress={handleVerParcelas}
            enabled={selectedDebits.length > 0}
          />
        </SafeAreaView>
      </Footer>
    </Container>
  );
}
